import { useState, useEffect } from "react";
import { scrollToTop } from "../formHelpers";

export function buildDocumentUploadFormValues(fieldNames) {
  const values = { note: "" };
  for (const name of fieldNames) {
    values[name] = null;
    values[`${name}Link`] = "";
    values[`${name}FileName`] = "";
  }
  return values;
}

export function buildDocumentUploadLoading(fieldNames) {
  return Object.fromEntries(fieldNames.map((name) => [name, false]));
}

export function hydrateDocumentUploadFromData(prevState, data, fieldNames) {
  if (!data) return prevState;

  const updated = { ...prevState, note: data.note || "" };
  for (const name of fieldNames) {
    updated[`${name}Link`] = data[`${name}Link`] || "";
    updated[`${name}FileName`] = data[`${name}FileName`] || "";
  }
  return updated;
}

async function defaultUploadToADLS(file) {
  const { apiClient } = await import("../../../../api/httpClient");
  const formData = new FormData();
  formData.append("file", file);
  formData.append("file_name", file.name);

  const response = await apiClient("/bond-file-upload/", "POST", formData, {}, {
    "Content-Type": "multipart/form-data",
  });

  if (!response.blob_link) {
    throw new Error("Blob link not returned from the server.");
  }

  return response.blob_link;
}

export default function useBondDocumentUploadState({
  data,
  updateData,
  fieldNames,
  uploadToADLS = defaultUploadToADLS,
  useFormHelperScroll = false,
  scrollOnMount = false,
}) {
  const [formValues, setFormValues] = useState(() =>
    buildDocumentUploadFormValues(fieldNames)
  );
  const [loading, setLoading] = useState(() => buildDocumentUploadLoading(fieldNames));
  const [removeConfirmOpen, setRemoveConfirmOpen] = useState(false);
  const [pendingRemoveName, setPendingRemoveName] = useState(null);

  useEffect(() => {
    setFormValues((prevState) =>
      hydrateDocumentUploadFromData(prevState, data, fieldNames)
    );
  }, [data, fieldNames]);

  useEffect(() => {
    if (scrollOnMount) {
      window.scrollTo(0, 0);
      return;
    }
    if (useFormHelperScroll) {
      scrollToTop();
    } else {
      window.scrollTo(0, 0);
    }
  }, [useFormHelperScroll, scrollOnMount]);

  const handleFileChange = async (e) => {
    const { name, files } = e.target;
    const file = files[0];

    if (!file) return;

    setFormValues((prevState) => ({ ...prevState, [name]: file }));
    setLoading((prevState) => ({ ...prevState, [name]: true }));

    try {
      const uploadedLink = await uploadToADLS(file);

      setFormValues((prevState) => ({
        ...prevState,
        [name]: file,
        [`${name}Link`]: uploadedLink,
        [`${name}FileName`]: file.name,
      }));

      updateData({
        ...data,
        [`${name}Link`]: uploadedLink,
        [`${name}FileName`]: file.name,
      });
    } catch (error) {
      console.error("Error uploading file:", error);
    } finally {
      setLoading((prevState) => ({ ...prevState, [name]: false }));
    }
  };

  const handleRemoveClick = (name) => {
    setPendingRemoveName(name);
    setRemoveConfirmOpen(true);
  };

  const handleRemoveConfirm = () => {
    if (pendingRemoveName) {
      setFormValues((prevState) => ({
        ...prevState,
        [pendingRemoveName]: null,
        [`${pendingRemoveName}Link`]: "",
        [`${pendingRemoveName}FileName`]: "",
      }));
      updateData({
        ...data,
        [`${pendingRemoveName}Link`]: "",
        [`${pendingRemoveName}FileName`]: "",
      });
    }
    setRemoveConfirmOpen(false);
    setPendingRemoveName(null);
  };

  const handleRemoveCancel = () => {
    setRemoveConfirmOpen(false);
    setPendingRemoveName(null);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormValues((prevState) => {
      const updatedFormValues = { ...prevState, [name]: value };
      updateData(updatedFormValues);
      return updatedFormValues;
    });
  };

  return {
    formValues,
    loading,
    handleFileChange,
    handleChange,
    removeConfirmOpen,
    pendingRemoveName,
    handleRemoveClick,
    handleRemoveConfirm,
    handleRemoveCancel,
  };
}
